import styled from 'styled-components';
import {
	LuTriangleAlert,
	LuCircleArrowOutUpRight,
	LuFileImage,
	LuMapPin,
} from 'react-icons/lu';

import CopyButton from '@routes/adminRoutes/sharedComponents/CopyButton';
import { formatNumber } from '@utils/helpers';
import { formatArea } from '@utils/measurementSystem';

const StyledOverlay = styled.div`
	background-color: ${props => props.theme.colors.background};
	padding: 10px 15px;
	font-size: 0.9rem;

	ul {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		gap: 20px;
		justify-content: space-between;
	}

	li {
		display: flex;
		align-items: center;
		gap: 5px;
	}

	button {
		font-size: 0.9rem;
		font-weight: 400;
		color: ${props => props.theme.colors.textColor};
	}
`;

const RotationArrow = styled(LuCircleArrowOutUpRight)`
	rotate: ${({ $rotation }) => $rotation - 45 || 0}deg;
`;

export const SelectedImageOverlayTooltip = ({ overlayContent }) => {
	if (!overlayContent) return null;

	const { name, rotation, center } = overlayContent;

	// Function to format coordinates to 5 decimal places
	const formatCoordinate = coord => parseFloat(coord.toFixed(5));

	// Format the center coordinates
	const formattedCenter = center.map(formatCoordinate).join(', ');

	return (
		<StyledOverlay>
			<ul>
				<li>
					<LuFileImage /> <span>{name}</span>
				</li>
				<li>
					<RotationArrow $rotation={rotation} />
					<span>{rotation}</span>
				</li>
				<li>
					<LuMapPin />
					<CopyButton
						value={formattedCenter}
						placement="right"
						className="d-inline w-auto">
						{formattedCenter}
					</CopyButton>
				</li>
			</ul>
		</StyledOverlay>
	);
};

const StyledImage = styled.img`
	max-width: 250px;
	max-height: 250px;
`;
const ClassItems = styled.div`
	padding: 5px 10px;
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	gap: 10px;
	font-size: 0.9rem;
	color: white;
	text-shadow: 1px 1px 1px black;
`;
const ColorDot = styled.span`
	width: 10px;
	height: 10px;
	display: inline-block;
	border-radius: 50%;
	margin-right: 2px;
	background-color: ${({ $r, $g, $b }) => `rgba(${$r}, ${$g}, ${$b}, 1)`};
	box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
`;
export const SingleImageOverlayTooltip = ({
	imageUrl,
	detectionClasses,
	colorScheme,
}) => {
	if (!imageUrl || !detectionClasses) return null;

	return (
		<div>
			<StyledImage src={imageUrl} />
			<ClassItems>
				<DetectionClasses
					detectionClasses={detectionClasses}
					colorScheme={colorScheme}
				/>
			</ClassItems>
		</div>
	);
};

const FootprintTooltipContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 5px;
	font-size: 0.9rem;
	color: ${props => props.theme.colors.textColor};
	.detection-class-item {
		display: flex;
		align-items: center;
		gap: 5px;
	}
	.small {
		font-size: 0.75rem;
	}
`;

export const FootprintTooltip = ({
	area,
	lrfActive,
	detectionClasses,
	colorScheme,
}) => {
	const [areaValue, areaUnit] = formatArea(area);

	return (
		<FootprintTooltipContainer>
			{area && (
				<div className="d-flex flex-row gap-2 pt-1">
					<span>
						{areaValue} {areaUnit}
					</span>
					{!lrfActive && (
						<span className="small text-warning d-flex align-items-center gap-1">
							<LuTriangleAlert /> Rough Estimate
						</span>
					)}
				</div>
			)}
			<div>
				<DetectionClasses
					detectionClasses={detectionClasses}
					colorScheme={colorScheme}
				/>
			</div>
		</FootprintTooltipContainer>
	);
};

const DetectionClasses = ({ detectionClasses, colorScheme }) => {
	if (!detectionClasses) return null;

	return (
		<>
			{detectionClasses?.map(item => {
				const { r, g, b } = colorScheme[item.id]?.color || {
					r: 0,
					g: 0,
					b: 0,
					a: 0,
				};
				return (
					<div key={item.id} className="detection-class-item">
						<ColorDot $r={r} $g={g} $b={b} />
						<span>{item.count}</span>
						{item.count > 0 && item.hectare_equivalent && (
							<HaEq hectareEquivalent={item.hectare_equivalent} />
						)}
					</div>
				);
			})}
		</>
	);
};

const HaEq = ({ hectareEquivalent }) => {
	if (typeof hectareEquivalent !== 'number') return null;

	return (
		<small className="text-muted">
			/ {formatNumber(hectareEquivalent, 0)} ha eq
		</small>
	);
};
